import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Check } from 'lucide-react';

export default function ComparisonModal({ isOpen, onClose, data, candidates }) {
    if (!isOpen || !data) return null;

    const { matrix, recommendation } = data;

    // Map candidate IDs to names for column headers
    // const candidateMap = candidates.reduce((acc, c) => ({...acc, [c.id]: c}), {});

    // Extract candidate IDs from the first row keys (excluding 'criteria')
    // Or we can just use the 'candidates' prop order if we trust it matches the response
    // Ideally the matrix keys are candidate_ids.
    // Let's assume matrix rows look like: { criteria: "Skills", "123": "Python", "456": "Java" }

    // const candidateIds = selectedCandidates.map(c => c.id);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.95, y: 20 }}
                    className="bg-white dark:bg-slate-900 w-full max-w-6xl h-[90vh] rounded-2xl shadow-xl overflow-hidden flex flex-col"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <Trophy className="text-yellow-500" /> Candidate Comparison
                            </h2>
                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">AI-powered analysis of top contenders</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors">
                            <X className="w-6 h-6 text-slate-500" />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-auto p-6">

                        {/* Recommendation Banner */}
                        {recommendation && (
                            <div className="mb-8 p-6 bg-gradient-to-r from-brand-50 to-purple-50 dark:from-brand-900/20 dark:to-purple-900/20 rounded-xl border border-brand-100 dark:border-brand-800">
                                <h3 className="font-bold text-brand-800 dark:text-brand-300 mb-2 flex items-center gap-2">
                                    <Check className="w-5 h-5" /> AI Recommendation
                                </h3>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-lg">
                                    {recommendation}
                                </p>
                            </div>
                        )}

                        {/* Comparison Table */}
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800">
                                        <th className="p-4 border-b border-slate-200 dark:border-slate-700 min-w-[200px] text-slate-500 dark:text-slate-400 font-medium uppercase text-xs tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-800 z-10 border-r">
                                            Criteria
                                        </th>
                                        {candidates.map(c => (
                                            <th key={c._id} className="p-4 border-b border-slate-200 dark:border-slate-700 min-w-[300px]">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 shadow-sm flex items-center justify-center font-bold text-brand-600 dark:text-brand-400 border border-slate-100 dark:border-slate-600">
                                                        {c.name.substring(0, 2).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-900 dark:text-white">{c.name}</div>
                                                        <div className="text-xs text-slate-500 font-normal">{c.years_experience}y • {c.skills?.[0]}</div>
                                                    </div>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-slate-900">
                                    {matrix.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                            <td className="p-5 border-b border-slate-100 dark:border-slate-800 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-white dark:bg-slate-900 border-r dark:border-slate-800">
                                                {row.criteria}
                                            </td>
                                            {candidates.map(c => {
                                                const cellData = row[c._id];
                                                // Handle potential legacy string response or new object response
                                                const text = typeof cellData === 'object' ? cellData?.text : cellData;
                                                const score = typeof cellData === 'object' ? cellData?.score : null;

                                                return (
                                                    <td key={c._id} className="p-5 border-b border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-400 leading-relaxed align-top">
                                                        {score !== null && score !== undefined && (
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                    <div
                                                                        className={`h-full rounded-full ${score >= 8 ? 'bg-green-500' : score >= 5 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                                        style={{ width: `${score * 10}%` }}
                                                                    />
                                                                </div>
                                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{score}/10</span>
                                                            </div>
                                                        )}
                                                        {text || (
                                                            <span className="text-slate-300 italic text-sm">N/A</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 text-right">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
